import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  DatePicker,
  Switch,
} from '@roger/ui-components';
import { updateOrderOrdersOrderIdPatch } from '../../services/api/sdk.gen';
import { useToast } from '../../hooks/useToastContext';
import {
  formatDateToISO,
  parseISOToLocalDate,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import type {
  OrderManagerItemResponse,
  OrderUpdate,
} from '../../services/api/types.gen';

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';

interface EditDatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orders: OrderManagerItemResponse[];
  activeTab: OrderManagerTabId;
  onSuccess: () => void;
}

const EditDatesModal: React.FC<EditDatesModalProps> = ({
  open,
  onOpenChange,
  orders,
  activeTab,
  onSuccess,
}) => {
  const { error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const isSingle = orders.length === 1;
  const firstOrder = orders[0];

  const [sentDate, setSentDate] = useState<Date | undefined>(
    isSingle && firstOrder?.sent_to_physician_date
      ? (parseISOToLocalDate(firstOrder.sent_to_physician_date) ?? undefined)
      : undefined
  );
  const [receivedDate, setReceivedDate] = useState<Date | undefined>(
    isSingle && firstOrder?.received_date
      ? (parseISOToLocalDate(firstOrder.received_date) ?? undefined)
      : undefined
  );
  const [physicianSignDate, setPhysicianSignDate] = useState<Date | undefined>(
    isSingle && firstOrder?.physician_sign_date
      ? (parseISOToLocalDate(firstOrder.physician_sign_date) ?? undefined)
      : undefined
  );
  const [requiredForBilling, setRequiredForBilling] = useState(
    firstOrder?.required_for_billing ?? true
  );
  const [requiredForBillingTouched, setRequiredForBillingTouched] =
    useState(false);

  // Mixed-subtype bulk edits conservatively hide signature-only fields.
  const allSignatureRequired = orders.every(o => o.signature_required);
  const canEditRequiredForBilling =
    orders.length > 0 && orders.every(o => o.can_edit_required_for_billing);

  const showSentDate =
    activeTab === 'pending-signature' || activeTab === 'received';
  const showSignatureFields = activeTab === 'received' && allSignatureRequired;
  const lockedRequiredForBillingMessage = isSingle
    ? `${firstOrder?.order_type_display_name ?? 'This order type'} is always required for billing.`
    : 'One or more selected order types are always required for billing.';

  const handleSave = async () => {
    setIsLoading(true);
    const body: OrderUpdate = {};
    if (showSentDate && sentDate)
      body.sent_to_physician_date = formatDateToISO(sentDate);
    if (showSignatureFields && receivedDate)
      body.received_date = formatDateToISO(receivedDate);
    if (showSignatureFields && physicianSignDate)
      body.physician_sign_date = formatDateToISO(physicianSignDate);
    if (requiredForBillingTouched && canEditRequiredForBilling) {
      body.required_for_billing = requiredForBilling;
    }

    if (Object.keys(body).length === 0) {
      setIsLoading(false);
      onOpenChange(false);
      return;
    }

    const results = await Promise.allSettled(
      orders.map(o =>
        updateOrderOrdersOrderIdPatch({
          path: { order_id: o.id },
          body,
        })
      )
    );
    const failedCount = results.filter(r => r.status === 'rejected').length;
    if (failedCount > 0) {
      console.error('Failed to update orders:', results);
      showError(
        'Update Failed',
        `Unable to update ${failedCount} of ${orders.length} orders. Please try again.`
      );
    }
    setIsLoading(false);
    onOpenChange(false);
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>
            Edit Order{orders.length > 1 ? `s (${orders.length} orders)` : ''}
          </DialogTitle>
          {orders.length === 1 && (
            <p className='text-sm text-muted-foreground pt-1'>
              {orders[0].patient_name} — {orders[0].order_type_display_name} (
              {orders[0].order_date
                ? (formatDateToMMDDYYYY(
                    parseISOToLocalDate(orders[0].order_date)
                  ) ?? orders[0].order_date)
                : ''}
              )
            </p>
          )}
        </DialogHeader>
        <div className='flex flex-col gap-4 py-2'>
          <div className='flex flex-col gap-1.5'>
            <div className='flex items-center justify-between gap-3'>
              <div>
                <label
                  htmlFor='required-for-billing'
                  className='text-sm font-medium'
                >
                  Required for Billing
                </label>
                <p className='text-xs text-muted-foreground'>
                  Required orders must be satisfied before a claim for this
                  billing cycle can be made.
                </p>
              </div>
              <Switch
                id='required-for-billing'
                checked={requiredForBilling}
                disabled={!canEditRequiredForBilling || isLoading}
                onCheckedChange={checked => {
                  setRequiredForBilling(checked);
                  setRequiredForBillingTouched(true);
                }}
              />
            </div>
            {!canEditRequiredForBilling && (
              <p className='text-xs text-muted-foreground'>
                {lockedRequiredForBillingMessage}
              </p>
            )}
          </div>
          {showSentDate && (
            <div className='flex flex-col gap-1.5'>
              <label className='text-sm font-medium'>
                Sent to Physician Date
              </label>
              <DatePicker
                date={sentDate}
                onDateChange={setSentDate}
                className='w-full'
              />
            </div>
          )}
          {showSignatureFields && (
            <div className='flex flex-col gap-1.5'>
              <label className='text-sm font-medium'>Received Date</label>
              <DatePicker
                date={receivedDate}
                onDateChange={setReceivedDate}
                className='w-full'
              />
            </div>
          )}
          {showSignatureFields && (
            <div className='flex flex-col gap-1.5'>
              <label className='text-sm font-medium'>Physician Sign Date</label>
              <DatePicker
                date={physicianSignDate}
                onDateChange={setPhysicianSignDate}
                className='w-full'
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditDatesModal;
